import React from "react";
import { capitalize } from "lodash";
import { getClassificationByLabels } from "../../../lib/enums/ThreatStatusEnum";
import { enums, route } from "../../../shim/jsbridge";
import { Timerange } from "../../search/@types";
import RetroHuntTooltipInfo from "./retrohunt/RetroHuntTooltipInfo";
import {
  ISourcesOptions,
  NamedThreatCount,
  ThreatCount,
  YaraDetailsQueryParams
} from "../@types";
import { PAGE_PATH } from "../../../path";

export const getDescriptionFromStatus = (status: number) => {
  switch (status) {
    case enums.yaraRulesetStatus.ERROR:
      return "Ruleset can not be processed due to errors.";
    case enums.yaraRulesetStatus.ACTIVE:
      return "Ruleset is active.";
    case enums.yaraRulesetStatus.DISABLED:
      return "Ruleset is disabled.";
    case enums.yaraRulesetStatus.DELETED:
      return "Ruleset has been deleted.";
    case enums.yaraRulesetStatus.PENDING:
      return "Ruleset is being validated.";
    case enums.yaraRulesetStatus.INVALID:
      return "Ruleset is invalid";
    case enums.yaraRulesetStatus.CAPPED:
      return "Maximum number of Spectra Intelligence matches reached. New Spectra Intelligence matches will not be stored";
    default:
      return "Unknown status.";
  }
};

export const getRetrohuntDescriptionFromStatus = (
  status: number,
  startTime: string | null,
  finishTime: string | null,
  estimatedFinishTime: string | null
) => {
  switch (status) {
    case enums.yaraRetroHuntStatus.IDLE:
      return "Ruleset is disabled.";
    case enums.yaraRetroHuntStatus.VALIDATING:
      return "Ruleset is being validated.";
    case enums.yaraRetroHuntStatus.ERROR:
      return "Ruleset can not be processed due to errors.";
    case enums.yaraRetroHuntStatus.READY:
      return "Ruleset is ready for YARA Retro Hunt";
    case enums.yaraRetroHuntStatus.RUNNING:
      return (
        <RetroHuntTooltipInfo
          title="YARA Retro Hunt running"
          labels={{ start: "Started", finish: "Estimated finish" }}
          startTime={startTime}
          endTime={estimatedFinishTime}
        />
      );
    case enums.yaraRetroHuntStatus.COMPLETE:
      return (
        <RetroHuntTooltipInfo
          title="YARA Retro Hunt completed"
          startTime={startTime}
          endTime={finishTime}
        />
      );
    case enums.yaraRetroHuntStatus.CANCELLED:
      return "YARA Retro Hunt has been cancelled.";
    case enums.yaraRetroHuntStatus.CLEARED:
      return "YARA Retro Hunt results have been cleared.";
    default:
      return "Unknown status.";
  }
};

export const getRetrohuntStateFromCode = (statusCode: number) => {
  switch (statusCode) {
    case enums.localRetroHistoryState.RUNNING:
      return "RUNNING";
    case enums.localRetroHistoryState.COMPLETED:
      return "COMPLETED";
    case enums.localRetroHistoryState.STOPPED:
      return "STOPPED";
    case enums.localRetroHistoryState.STARTING:
      return "STARTING";
    default:
      return "UNKNOWN";
  }
};

export const sortFieldMappings: { [key: string]: string } = {
  yara_availability: "location",
  sample_size: "file_size",
  match_time: "created",
  yara_rule: "rule",
  sha1: "filename",
  sample_type: "type_display"
};

export const shortenTickLabel = (tick: string) => {
  if (tick.length > 11) {
    return `${tick.slice(0, 8)}...`;
  }
  return tick;
};

export const convertThreatStatusIdToLabel = (threatStatusId: number) => {
  switch (threatStatusId) {
    case enums.threat_status.GOODWARE:
      return "Goodware";
    case enums.threat_status.MALICIOUS:
      return "Malicious";
    case enums.threat_status.SUSPICIOUS:
      return "Suspicious";
    case enums.threat_status.UNKNOWN:
      return "Unknown";
    default:
      return "--";
  }
};

export const convertThreatCountsToChartValues = (
  threatCounts?: ThreatCount[]
) =>
  threatCounts?.map(threatCount => {
    const threatLabel = convertThreatStatusIdToLabel(threatCount.threat);
    return {
      x: threatLabel,
      y: threatCount.count,
      label: `${threatCount.count} ${threatLabel}`
    };
  });

export const getTodaysDate = () => {
  const todaysDate = new Date();

  todaysDate.setHours(23);
  todaysDate.setMinutes(59);
  todaysDate.setSeconds(0);
  todaysDate.setMilliseconds(0);

  return todaysDate;
};

export const getFilterParams = (
  rulesetId: number,
  ruleId?: string | null,
  classificationName?: string | null,
  fileFormatName?: string | null,
  threatTypeName?: string | null,
  timerange?: Timerange,
  sortField?: string,
  sortDirection?: string
) => ({
  id: rulesetId,
  ...(ruleId != null && { rule_id: ruleId }),
  ...(sortField != null && { "sort[0][field]": sortField }),
  ...(sortDirection != null && { "sort[0][dir]": sortDirection }),
  query: JSON.stringify({
    ...(classificationName != null && {
      threats: [getClassificationByLabels(classificationName)]
    }),
    ...(fileFormatName != null && {
      formats: [{ name: fileFormatName }]
    }),
    ...(threatTypeName != null && { threat_types: [threatTypeName] }),
    ...(timerange != null && { timerange })
  })
});

const removeEmptyFields = (params: object) =>
  Object.entries(params).reduce((previousValue, [currentKey, currentValue]) => {
    if (currentValue != null) {
      previousValue[currentKey] = currentValue;

      return previousValue;
    } else {
      return previousValue;
    }
  }, {} as Record<string, unknown>);

export const exportAllYaraSamples = (
  rulesetId: number,
  ruleId?: string | null,
  classificationName?: string | null,
  fileFormatName?: string | null,
  threatTypeName?: string | null,
  timerange?: Timerange,
  sortField?: string,
  sortDirection?: string
) =>
  window.open(
    route(
      "api-yara-sample-export-all",
      {},
      removeEmptyFields({
        revision_id: rulesetId,
        rule_id: ruleId,
        timerange_start: timerange?.start,
        timerange_end: timerange?.end,
        threat_type_name: threatTypeName,
        classification_id: getClassificationByLabels(classificationName),
        file_format_name: fileFormatName,
        sort_field: sortField,
        sort_dir: sortDirection
      })
    ),
    "_blank"
  );

export const exportYaraSamplesByHash = (
  rulesetId: number,
  hashes: string[],
  sortField?: string,
  sortDirection?: string
) =>
  window.open(
    route(
      "api-yara-sample-export-by-hashes",
      {},
      {
        revision_id: rulesetId,
        hashes,
        sort_field: sortField,
        sort_dir: sortDirection
      }
    ),
    "_blank"
  );

export const createDropdownMenuItems = (
  items: string[] = [],
  allItemLabel = "All"
) => [
  {
    label: allItemLabel,
    value: null
  },
  ...items.filter(Boolean).map((item: string) => ({
    label: item,
    value: item
  }))
];

export const isActionDestructive = (actionLabel: string | undefined) => {
  if (!actionLabel) return false;
  return [
    enums.yaraRulesetActions.STOPRETROHUNT,
    enums.yaraRulesetActions.CLEARRETROHUNT,
    enums.yaraRulesetActions.DISABLE,
    enums.yaraRulesetActions.DELETE,
    enums.yaraRulesetActions.UNSUBSCRIBE
  ].includes(actionLabel);
};

export const getLabelPreposition = (label: string | undefined) => {
  switch (label) {
    case enums.yaraRulesetActions.RUNRETROHUNT:
    case enums.yaraRulesetActions.STOPRETROHUNT:
    case enums.yaraRulesetActions.CLEARRETROHUNT:
      return "for";
    case enums.yaraRulesetActions.UNSUBSCRIBE:
      return "from";
    default:
      return "";
  }
};

export const getOnlineSourceType = (url: string | undefined) => {
  if (!url) return "";
  const domain = url
    .split("://")[1]
    .split("/")[0]
    .replace("www.", "")
    .split(".")[0];
  switch (domain) {
    case "github":
      return "GitHub";
    default:
      return capitalize(domain);
  }
};

export const checkIfActiveSourceFilterExists = (
  activeItem?: string,
  items?: ISourcesOptions
): boolean => {
  if (!items || !activeItem) return false;

  return Object.values(items)
    .flat()
    .some(item => item.value === activeItem);
};

export const generateYaraDetailsLink = (
  rulesetId: number,
  queryParams?: YaraDetailsQueryParams
): string => {
  if (!rulesetId) return "";

  const queryString = Object.entries(queryParams ?? {})
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

  return (
    PAGE_PATH.YARA_DETAILS.replace(":rulesetId", rulesetId.toString()) +
    (queryParams != null ? `?${queryString}` : "")
  );
};

export const sortFileFormatsByTotalCount = (
  fileFormats: NamedThreatCount[] | undefined
) => {
  if (!fileFormats) return undefined;

  const withTotals = fileFormats.map(format => {
    const recordFormat = format as unknown as Record<string, any>;

    const totalCount = Object.keys(recordFormat)
      .filter(key => key.startsWith("total_"))
      .reduce((sum, key) => sum + recordFormat[key], 0);

    return {
      format,
      totalCount,
      isOther: format.name === "Other"
    };
  });

  const hasOther = withTotals.some(item => item.isOther);

  return withTotals
    .sort((a, b) => {
      if (hasOther) {
        if (a.isOther) return 1;
        if (b.isOther) return -1;
      }
      return b.totalCount - a.totalCount;
    })
    .map(item => item.format);
};
